/**
 * Chip text-color contrast utility and category palette helpers.
 *
 * The contrast logic mirrors the Rust implementation in
 * client/client_core/src/model_visualizer/view_item_viz/table_view_renders/render_content.rs
 *
 * Palette values are derived at import time from tokens.json (the shared source
 * of truth with common_core/build.rs on the Rust side).
 */

import tokens from "../../../../tokens.json";

/** A single category entry as returned by the WASM client. */
export interface CategoryItem {
  id: number;
  label: string;
  color: string | null;
}

type HslTuple = [number, number, number];

/** Parse "hsl(H, S%, L%)". Mirror of `parseHsl()` in scripts/generate-tokens.mjs. */
function parseHsl(hslStr: string): HslTuple {
  const m = hslStr.match(
    /hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/,
  );
  if (!m) throw new Error(`Cannot parse HSL: "${hslStr}"`);
  return [parseFloat(m[1]), parseFloat(m[2]) / 100, parseFloat(m[3]) / 100];
}

/** Convert "hsl(...)" to "#rrggbb". Mirror of `hslToHex()` in scripts/generate-tokens.mjs
 *  and `hsl_to_hex()` in common_core/build.rs — keep in sync. */
function hslToHex(hslStr: string): string {
  const [h, s, l] = parseHsl(hslStr);
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r: number, g: number, b: number;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

type ColorEntry = { light: string; dark: string };

const colors = (tokens.shared?.colors ?? {}) as Record<string, ColorEntry>;

export interface PaletteEntry {
  readonly name: string;
  readonly light: string;
  readonly dark: string;
}

export const CATEGORY_PALETTE: readonly PaletteEntry[] = Object.entries(colors)
  .filter(([name]) => name.startsWith("cat-") && name !== "cat-default")
  .map(([name, val]) => ({
    name: name.slice("cat-".length),
    light: hslToHex(val.light),
    dark: hslToHex(val.dark),
  }));

const catDefault = colors["cat-default"];
if (!catDefault) {
  throw new Error("tokens.json is missing shared.colors['cat-default']");
}

export const CAT_DEFAULT: { readonly light: string; readonly dark: string } = {
  light: hslToHex(catDefault.light),
  dark: hslToHex(catDefault.dark),
};

export const LIGHT_TO_DARK: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const p of CATEGORY_PALETTE) map[p.light.toLowerCase()] = p.dark;
  map[CAT_DEFAULT.light.toLowerCase()] = CAT_DEFAULT.dark;
  return map;
})();

/** ITU-R BT.709 relative luminance from a hex color string. */
export function hexRelativeLuminance(hex: string): number {
  const h = hex.replace(/^#/, "");
  if (h.length < 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Text colors used on chips. These mirror --color-fg (~#1a1f2b) and --color-bg
 * (~#f7f8fa) but are pinned as hex so the canvas renderer (no CSS-var access)
 * stays in sync with the DOM.
 */
const CHIP_TEXT_ON_LIGHT = "#1a1f2b";
const CHIP_TEXT_ON_DARK = "#f7f8fa";

/**
 * Return a readable text color for the given chip background.
 * Light backgrounds get near-black, dark backgrounds get near-white.
 * Matches the Rust `chip_text_color()` function exactly.
 */
export function chipTextColor(bgHex: string): string {
  return hexRelativeLuminance(bgHex) > 0.5
    ? CHIP_TEXT_ON_LIGHT
    : CHIP_TEXT_ON_DARK;
}

/**
 * Resolve the display background for a chip, remapping palette light→dark in
 * dark mode. Non-palette (custom) colors are returned as-is.
 */
export function resolveChipBg(
  storedColor: string | null,
  isDark: boolean,
): string {
  const fallback = isDark ? CAT_DEFAULT.dark : CAT_DEFAULT.light;
  if (!storedColor) return fallback;
  if (!isDark) return storedColor;
  return LIGHT_TO_DARK[storedColor.toLowerCase()] ?? storedColor;
}

/** Return the 12 palette hex values for the current theme. */
export function paletteColors(isDark: boolean): string[] {
  return CATEGORY_PALETTE.map((e) => (isDark ? e.dark : e.light));
}

/** A new category's colour: the first light hex no category wears yet. */
export function nextCategoryColor(categories: readonly CategoryItem[]): string {
  const colors = paletteColors(false);
  const used = new Set(categories.map((c) => c.color));
  return colors.find((c) => !used.has(c)) ?? colors[0];
}

// ── Category-ID string codec ────────────────────────────────────────────────
//
// The WASM boundary transports category IDs through `CellValue` edits as a
// delimited string (one ID for single-select, comma-joined IDs for multi).

const CATEGORY_ID_SEPARATOR = ",";

export function serializeCategoryIds(ids: Iterable<number>): string {
  return Array.from(ids).join(CATEGORY_ID_SEPARATOR);
}

export function parseCategoryIds(raw: string | null | undefined): number[] {
  if (!raw || !raw.trim()) return [];
  return raw
    .split(CATEGORY_ID_SEPARATOR)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

// ── Category lookups (wrappers around the WASM client) ──────────────────────

type CategoryClient = {
  get_column_categories: (tableId: string, columnId: string) => unknown;
};

/**
 * Read the current categories for a column from the WASM client, returning
 * `[]` on any error or when the client is not yet available.
 */
export function getColumnCategories(
  clientApp: CategoryClient | null,
  tableId: string,
  columnId: string,
): CategoryItem[] {
  if (!clientApp) return [];
  try {
    return (
      (clientApp.get_column_categories(tableId, columnId) as
        CategoryItem[] | null) ?? []
    );
  } catch {
    return [];
  }
}

/**
 * Build `{ columnId: { categoryId: label } }` mappings for every Categorical
 * column in `columns`. Used by the export worker to resolve IDs to labels.
 */
export function buildCategoryLabels(
  clientApp: CategoryClient | null,
  tableId: string,
  columns: Array<{ id: string; data_type: unknown }>,
  categoricalType: unknown,
): Record<string, Record<number, string>> {
  const labels: Record<string, Record<number, string>> = {};
  if (!clientApp) return labels;
  for (const col of columns) {
    if (col.data_type !== categoricalType) continue;
    const cats = getColumnCategories(clientApp, tableId, col.id);
    if (cats.length === 0) continue;
    const mapping: Record<number, string> = {};
    for (const entry of cats) mapping[entry.id] = entry.label;
    labels[col.id] = mapping;
  }
  return labels;
}
